import React, { useState, useEffect, useMemo } from "react";
import Papa from "papaparse";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Upload,
  Users,
  MapPin,
  Building,
  Search,
  ExternalLink,
  Instagram,
  Linkedin,
  Filter,
  RefreshCw,
  FileText,
  TrendingUp,
  Home,
  DollarSign,
} from "lucide-react";

// --- Dark Mode Card Component ---
const Card = ({ children, className = "" }) => (
  <div
    className={`bg-slate-900 rounded-xl shadow-lg border border-slate-800 p-6 ${className}`}
  >
    {children}
  </div>
);

// --- KPI Component ---
const KPI = ({
  icon: Icon,
  label,
  value,
  subtext,
  color = "text-blue-400",
  bg = "bg-blue-900/20",
}) => (
  <Card>
    <div className="flex items-center space-x-4">
      <div className={`p-3 rounded-lg ${bg} ${color}`}>
        <Icon size={24} />
      </div>
      <div>
        <p className="text-sm font-medium text-slate-400">{label}</p>
        <h3 className="text-2xl font-bold text-white">{value}</h3>
        {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
      </div>
    </div>
  </Card>
);

const COLORS = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#8b5cf6",
  "#ec4899",
  "#6366f1",
];

export default function App() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fileName, setFileName] = useState("new_england_realtors.csv");

  // --- Filters ---
  const [searchTerm, setSearchTerm] = useState("");
  const [stateFilter, setStateFilter] = useState("All");
  const [signalFilter, setSignalFilter] = useState("All"); // "All", "Sold/Closing", "New Listing", etc.

  // --- 1. Load Data ---
  useEffect(() => {
    fetch("/new_england_realtors.csv")
      .then((response) => {
        if (!response.ok) throw new Error("Default file not found");
        return response.text();
      })
      .then((csvText) => {
        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            if (results.data && results.data.length > 0) {
              setData(results.data);
            }
            setLoading(false);
          },
        });
      })
      .catch((err) => {
        console.error("Error loading CSV:", err);
        setLoading(false);
      });
  }, []);

  // --- 2. Handle File Upload ---
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLoading(true);
      setFileName(file.name);
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          setData(results.data);
          setLoading(false);
        },
      });
    }
  };

  // --- 3. Compute Analytics ---
  const analytics = useMemo(() => {
    if (!data.length)
      return {
        stateData: [],
        platformData: [],
        uniqueBrokerages: 0,
        topRegion: "N/A",
      };

    const stateCounts = {};
    data.forEach((row) => {
      const rawState = row["State Region"] || "Unknown";
      rawState.split(",").forEach((s) => {
        const state = s.trim();
        if (state && state !== "nan")
          stateCounts[state] = (stateCounts[state] || 0) + 1;
      });
    });

    const stateData = Object.keys(stateCounts)
      .map((key) => ({ name: key, count: stateCounts[key] }))
      .sort((a, b) => b.count - a.count);

    const platformCounts = {};
    data.forEach((row) => {
      const plat = row["Source Platform"] || "Other";
      platformCounts[plat] = (platformCounts[plat] || 0) + 1;
    });
    const platformData = Object.keys(platformCounts).map((key) => ({
      name: key,
      value: platformCounts[key],
    }));

    const uniqueBrokerages = new Set();
    data.forEach((d) => {
      if (d["Detected Brokerage"]) {
        d["Detected Brokerage"].split(",").forEach((b) => {
          if (b.trim().length > 2) uniqueBrokerages.add(b.trim());
        });
      }
    });

    return {
      stateData,
      platformData,
      uniqueBrokerages: uniqueBrokerages.size,
      topRegion: stateData[0]?.name || "N/A",
    };
  }, [data]);

  // --- 4. Filter Logic ---
  const filteredData = data.filter((row) => {
    const matchesSearch =
      (row["Title"] || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (row["Snippet"] || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (row["Detected Brokerage"] || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesState =
      stateFilter === "All" ||
      (row["State Region"] || "").includes(stateFilter);

    const matchesSignal =
      signalFilter === "All" ||
      (row["Activity Type"] || "").includes(signalFilter);

    return matchesSearch && matchesState && matchesSignal;
  });

  // Get Top 4 States for Quick Filters
  const topStates = analytics.stateData.slice(0, 4).map((s) => s.name);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-400">
        <RefreshCw className="animate-spin mr-2" /> Loading Dashboard...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-8 font-sans text-slate-100">
      {/* --- Header --- */}
      <div className="max-w-7xl mx-auto mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-800 pb-6">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">
            New England Realtor Harvester
          </h1>
          <div className="flex items-center space-x-2 mt-2">
            <div className="flex items-center px-2 py-1 rounded bg-slate-900 border border-slate-800 text-xs text-slate-400">
              <FileText size={12} className="mr-1" />
              <span>{fileName}</span>
            </div>
          </div>
        </div>

        {/* Upload Button */}
        <div className="relative group">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
          />
          <button className="flex items-center space-x-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 px-4 py-2 rounded-lg transition text-sm font-medium z-10">
            <Upload size={16} />
            <span>Load New File</span>
          </button>
        </div>
      </div>

      {!data.length ? (
        <div className="max-w-7xl mx-auto h-96 border-2 border-dashed border-slate-800 rounded-xl flex flex-col items-center justify-center text-slate-500 bg-slate-900/50">
          <Upload size={48} className="mb-4 text-slate-600" />
          <p className="text-lg">No data loaded.</p>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto space-y-6">
          {/* --- KPIs --- */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <KPI
              icon={Users}
              label="Total Leads Found"
              value={filteredData.length}
              subtext="Based on current filters"
              color="text-blue-400"
              bg="bg-blue-500/10"
            />
            <KPI
              icon={MapPin}
              label="Top Region (All Data)"
              value={analytics.topRegion}
              subtext="Highest volume state"
              color="text-emerald-400"
              bg="bg-emerald-500/10"
            />
            <KPI
              icon={Building}
              label="Brokerages Detected"
              value={analytics.uniqueBrokerages}
              subtext="Agencies identified"
              color="text-purple-400"
              bg="bg-purple-500/10"
            />
          </div>

          {/* --- Charts --- */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <h3 className="font-semibold mb-4 text-slate-200">
                Leads by State
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.stateData.slice(0, 10)}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#334155"
                    />
                    <XAxis
                      dataKey="name"
                      stroke="#94a3b8"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      stroke="#94a3b8"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      cursor={{ fill: "#1e293b" }}
                      contentStyle={{
                        backgroundColor: "#0f172a",
                        borderColor: "#334155",
                        color: "#f1f5f9",
                        borderRadius: "8px",
                      }}
                    />
                    <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card>
              <h3 className="font-semibold mb-4 text-slate-200">
                Platform Source
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analytics.platformData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {analytics.platformData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                          stroke="rgba(0,0,0,0)"
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#0f172a",
                        borderColor: "#334155",
                        color: "#f1f5f9",
                        borderRadius: "8px",
                      }}
                    />
                    <Legend wrapperStyle={{ color: "#94a3b8" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          {/* --- Filter & Table Section --- */}
          <Card className="overflow-hidden border-t-4 border-t-blue-500">
            {/* Control Bar */}
            <div className="flex flex-col space-y-4 mb-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <h3 className="font-bold text-lg text-white">Agent Database</h3>

                {/* Search */}
                <div className="relative w-full md:w-64">
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500"
                  />
                  <input
                    type="text"
                    placeholder="Search agents..."
                    className="pl-9 pr-4 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-blue-500 w-full"
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="h-px bg-slate-800 w-full"></div>

              {/* Filters Row */}
              <div className="flex flex-col lg:flex-row gap-6">
                {/* 1. Quick State Toggles */}
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Region
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setStateFilter("All")}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                        stateFilter === "All"
                          ? "bg-blue-600 text-white"
                          : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                      }`}
                    >
                      All
                    </button>
                    {topStates.map((state) => (
                      <button
                        key={state}
                        onClick={() => setStateFilter(state)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                          stateFilter === state
                            ? "bg-blue-600 text-white"
                            : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                        }`}
                      >
                        {state}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 2. Signal Type Toggles */}
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Signal Type
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: "All", label: "All Signals", icon: null },
                      { id: "Sold", label: "Sold", icon: DollarSign },
                      { id: "Listing", label: "Listings", icon: Home },
                      {
                        id: "Market",
                        label: "Market Updates",
                        icon: TrendingUp,
                      },
                    ].map((sig) => (
                      <button
                        key={sig.id}
                        onClick={() =>
                          setSignalFilter(sig.id === "All" ? "All" : sig.id)
                        }
                        className={`flex items-center space-x-1 px-3 py-1.5 rounded-full text-xs font-medium transition ${
                          signalFilter === sig.id ||
                          (sig.id !== "All" && signalFilter.includes(sig.id))
                            ? "bg-emerald-600 text-white"
                            : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                        }`}
                      >
                        {sig.icon && <sig.icon size={12} />}
                        <span>{sig.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-400 text-xs uppercase tracking-wider">
                    <th className="py-3 px-4 font-semibold">Source</th>
                    <th className="py-3 px-4 font-semibold">Agent / Title</th>
                    <th className="py-3 px-4 font-semibold">Region</th>
                    <th className="py-3 px-4 font-semibold">Signal</th>
                    <th className="py-3 px-4 font-semibold">Date</th>
                    <th className="py-3 px-4 font-semibold text-right">Link</th>
                  </tr>
                </thead>
                <tbody className="text-sm divide-y divide-slate-800">
                  {filteredData.slice(0, 100).map((row, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-slate-800/50 transition group"
                    >
                      <td className="py-3 px-4">
                        {row["Source Platform"] === "LinkedIn" ? (
                          <Linkedin size={18} className="text-blue-500" />
                        ) : (
                          <Instagram size={18} className="text-pink-500" />
                        )}
                      </td>
                      <td className="py-3 px-4 max-w-xs">
                        <div
                          className="font-medium text-slate-200 truncate"
                          title={row.Title}
                        >
                          {row.Title}
                        </div>
                        {row["Detected Brokerage"] && (
                          <div className="text-xs text-slate-500 truncate flex items-center mt-1">
                            <Building size={12} className="mr-1" />{" "}
                            {row["Detected Brokerage"]}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-block px-2 py-0.5 rounded border border-slate-700 bg-slate-800 text-slate-300 text-xs font-medium">
                          {row["State Region"]}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${
                            (row["Activity Type"] || "").includes("Listing")
                              ? "bg-emerald-900/30 border-emerald-800 text-emerald-400"
                              : (row["Activity Type"] || "").includes("Sold")
                              ? "bg-purple-900/30 border-purple-800 text-purple-400"
                              : "bg-slate-800 border-slate-700 text-slate-400"
                          }`}
                        >
                          {row["Activity Type"]}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-500 whitespace-nowrap text-xs">
                        {row["Date Found"] || "-"}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <a
                          href={row.URL}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-400 hover:text-blue-300 inline-flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <span className="text-xs">View</span>
                          <ExternalLink size={12} />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-4 text-center text-xs text-slate-500 border-t border-slate-800">
                Showing {Math.min(filteredData.length, 100)} of{" "}
                {filteredData.length} matches
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
